import { IconButton, Snackbar, SnackbarContent } from "@mui/material";
import { TFunction } from "i18next";
import { AlertVariant } from "../Alerts/AlertDefault";
import ButtonDefault from "../Button/ButtonDefault";
import {
  AlertIcon,
  CloseIcon,
  CompleteIcon,
  ErrorIcon,
  InfoIcon,
  SyncErrorIcon,
  SyncIcon,
} from "../Icons/Icons";
import Text, { TextVariant } from "../Text/Text";

export type SnackbarDefaultProps = {
  message: string;
  onClose: () => void;
  undoFn?: () => void;
  autoHideDuration?: number;
  t: TFunction<"translation", undefined>;
  severity: AlertVariant;
} & React.ComponentProps<"div">;

export default function SnackbarDefault({
  message,
  onClose,
  undoFn,
  autoHideDuration = 7000,
  t,
  severity,
}: SnackbarDefaultProps): JSX.Element {
  const defaultCloseAction = (
    <IconButton size="small" color="inherit" onClick={() => onClose()}>
      <CloseIcon />
    </IconButton>
  );

  const undoAction = undoFn && (
    <div className="w-full mt-4 px-2">
      <ButtonDefault
        className="p-3"
        id="angre"
        label={t("actions.undo")}
        onClick={() => {
          undoFn && undoFn();
          onClose();
        }}
      />
    </div>
  );

  let color;
  switch (severity) {
    case AlertVariant.Sync:
    case AlertVariant.Info:
      color = "bg-ldir-blue-500";
      break;

    case AlertVariant.Warning:
      color = "bg-ldir-orange-700";
      break;

    case AlertVariant.Error:
    case AlertVariant.SyncError:
      color = "bg-ldir-red-700";
      break;

    case AlertVariant.Success:
      color = "bg-ldir-green-700";
  }

  return (
    <Snackbar
      open={true}
      autoHideDuration={autoHideDuration}
      onClose={(e) => {
        if (e && e.type === "click") {
          e.preventDefault(); // ignore "click-away"
          return;
        }
        onClose();
      }}
      message={message}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      disableWindowBlurListener={true}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        "& .MuiSnackbarContent-message": {
          width: "100%",
          display: "block",
        },
      }}
    >
      <SnackbarContent
        className={`opacity-100 text-ldir-brown-900 ${color} w-full`}
        sx={{ borderRadius: 0, width: "100%" }}
        message={
          <>
            <div className="w-full flex justify-between">
              <div className="flex items-start">
                {severity === AlertVariant.Info ? (
                  <InfoIcon />
                ) : severity === AlertVariant.Success ? (
                  <CompleteIcon />
                ) : severity === AlertVariant.Warning ? (
                  <AlertIcon className="text-ldir-orange-900" />
                ) : severity === AlertVariant.Error ? (
                  <ErrorIcon />
                ) : severity === AlertVariant.Sync ? (
                  <SyncIcon className="text-ldir-blue-900" />
                ) : (
                  <SyncErrorIcon className="text-ldir-red-900" />
                )}
                <Text variant={TextVariant.small} className="ml-2 whitespace-pre-line">
                  {message}
                </Text>
              </div>
              {defaultCloseAction}
            </div>
            {undoAction}
          </>
        }
      />
    </Snackbar>
  );
}
